import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { TReactSetState, TScrollEvent } from "../../data/AppType";
import { IPagination } from "../../data/AppInterface";
import UsePaginationHook from "../../hooks/UsePaginationHook";
import {
  deleteGalleryDirectoryItem,
  fetchGalleryDirectoryItemList,
  IGalleryDirectoryItemList,
} from "./GalleryItemController";
import ICImage from "../../core-component/ui/ICImage";
import {
  AdvanceSearch,
  ICConfirmationModal,
  ICModal,
} from "../../core-component/ui";
import GalleryDirectoryFormItem from "./GalleryDirectoryItemForm";
import Loader from "../../core-component/ui/Loader";
import BufferLoder from "../../core-component/ui/ICBufferLoder";
import { deleteIcon } from "../../assets/icons/SvgIconList";
import UseTranslationHook from "../../hooks/UseTranslationHook";
import { PAGINATION_PER_PAGE_ROWS } from "../../utils/AppConstants";

let timer: ReturnType<typeof setTimeout>;

const GalleryDirectoryItemList = () => {
  const { id: galleryDirectoryId } = useParams();
  const [loading, setLoading] = useState(true);
  const [bufferLoading, setBufferLoading] = useState(false);
  const [galleryDirectoryItemList, setGalleryDirectoryItemList] = useState<
    IGalleryDirectoryItemList[]
  >([]);
  const [showForm, setShowForm] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const { pagination, setChangePagination } = UsePaginationHook({
    pagination: {
      perPageRows: PAGINATION_PER_PAGE_ROWS.Twenty,
      currentPage: 1,
      sortBy: "id",
      orderBy: "DESC",
    },
  });
  const listInnerRef = useRef<HTMLDivElement>(null);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const { t } = UseTranslationHook();

  const fetchGalleryDirectoryItemListData = async (
    objPagination: IPagination,
    setLoading: TReactSetState<boolean>,
    isScroll: boolean,
  ) => {
    if (timer) {
      clearTimeout(timer);
    }
    timer = setTimeout(async () => {
      if (galleryDirectoryId) {
        await fetchGalleryDirectoryItemList(
          galleryDirectoryId,
          setLoading,
          setGalleryDirectoryItemList,
          setChangePagination,
          objPagination,
          galleryDirectoryItemList,
          isScroll,
        );
      }
    }, 500);
  };

  useEffect(() => {
    fetchGalleryDirectoryItemListData(pagination, setLoading, false);
  }, []);

  const onScroll = (event: TScrollEvent) => {
    if (listInnerRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = listInnerRef.current;
      const isNearBottom = scrollTop + clientHeight + 1 >= scrollHeight;

      if (
        !loading &&
        isNearBottom &&
        galleryDirectoryItemList &&
        pagination.totalItems > galleryDirectoryItemList.length
      ) {
        fetchGalleryDirectoryItemListData(
          {
            ...pagination,
            currentPage: pagination.currentPage + 1,
          },
          setBufferLoading,
          true,
        );
        event.preventDefault();
      }
    }
  };

  useEffect(() => {
    const listInnerElement = listInnerRef.current;
    if (listInnerElement) {
      listInnerElement.addEventListener(
        "scroll",
        onScroll as unknown as EventListener,
      );
    }

    return () => {
      if (listInnerElement)
        listInnerElement.removeEventListener(
          "scroll",
          onScroll as unknown as EventListener,
        );
    };
  }, [pagination]);

  const handleCloseForm = (status: boolean) => {
    setShowForm(false);
    if (status) {
      fetchGalleryDirectoryItemListData(
        { ...pagination, currentPage: 1 },
        setLoading,
        false,
      );
    }
  };

  const handleDelete = async (id: string | null) => {
    if (id) {
      const data = await deleteGalleryDirectoryItem(id);
      if (data) {
        fetchGalleryDirectoryItemListData(
          { ...pagination, currentPage: 1 },
          setLoading,
          false,
        );
      }
      setShowDeleteConfirmation(false);
      setSelectedId(null);
    }
  };

  const handleDeleteIconClick = (id: string) => {
    setSelectedId(id);
    setShowDeleteConfirmation(true);
  };

  const handleImageClick = (mediaUrl: string) => {
    setSelectedImage(mediaUrl);
  };

  const handleCloseModal = () => {
    setSelectedImage(null);
  };

  return (
    <>
      {loading ? (
        <Loader />
      ) : (
        <div className="p-4 overflow-hidden h-full flex flex-col">
          <AdvanceSearch
            backButton
            title={
              t("gallery_directory_item") +
              ` (${galleryDirectoryItemList.length})`
            }
            handleOnClickAdd={() => setShowForm(true)}
            refreshTable={() => {
              fetchGalleryDirectoryItemListData(
                { ...pagination, currentPage: 1 },
                setLoading,
                false,
              );
            }}
          />
          <div className="overflow-y-scroll mt-4 flex-1" ref={listInnerRef}>
            <div className="grid 3xl:grid-cols-8 xl:grid-cols-6 grid-cols-4 gap-4">
              {galleryDirectoryItemList &&
              galleryDirectoryItemList.length > 0 ? (
                galleryDirectoryItemList.map((item, index) => (
                  <div key={item.id} className="relative h-full w-full ">
                    <ICImage
                      containerClass="bg-subtle aspect-square"
                      imageUrl={item.media_url}
                      className="w-full aspect-square object-contain cursor-pointer"
                      onClick={() => handleImageClick(item.media_url)}
                    />
                    <div
                      className="absolute top-1 right-2 size-10 text-danger bg-danger-muted rounded-full p-2 cursor-pointer"
                      onClick={() => handleDeleteIconClick(item.id)}
                    >
                      {deleteIcon}
                    </div>
                  </div>
                ))
              ) : (
                <>{t("no_data")}</>
              )}
              {bufferLoading && (
                <div className="flex justify-center items-center">
                  <BufferLoder />
                </div>
              )}
            </div>
          </div>

          {showForm && galleryDirectoryId && (
            <GalleryDirectoryFormItem
              handleClose={handleCloseForm}
              galleryDiretoryId={galleryDirectoryId}
            />
          )}

          {showDeleteConfirmation && selectedId && (
            <ICConfirmationModal
              title={t("delete_gallery_item")}
              message={t("delete_gallery_item_confirm")}
              onClose={(confirm) => {
                if (confirm) {
                  handleDelete(selectedId);
                } else {
                  setShowDeleteConfirmation(false);
                }
              }}
            />
          )}

          {selectedImage && (
            <ICModal
              title={t("image_preview")}
              onClose={handleCloseModal}
              containerClass="max-w-5xl"
            >
              <ICImage
                containerClass="bg-subtle flex-1 max-h-[calc(80vh-32px)] aspect-square"
                imageUrl={selectedImage}
                className="w-full h-full aspect-square object-contain"
              />
            </ICModal>
          )}
        </div>
      )}
    </>
  );
};

export default GalleryDirectoryItemList;
